import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { PasswordInput } from '../components/ui';

/**
 * In-app account deletion — required by Apple (App Review 5.1.1(v)); linked from the
 * menu in the iOS app only. Asks for the password and the word DELETE, then erases the
 * account's personal data on the server and signs out.
 */
export default function DeleteAccount() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const ready = password.length > 0 && confirmText.trim().toUpperCase() === 'DELETE';

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setError('');
    setLoading(true);
    try {
      await api.post('/user/delete-account', { password });
      logout();
      navigate('/login', { replace: true });
      setTimeout(() => alert('Your account has been deleted.'), 50);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not delete the account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md py-6">
      <h1 className="text-2xl font-bold tracking-tight">Delete account</h1>
      <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
        <p className="font-semibold">This permanently deletes your StockXignal account.</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>Your name, email, phone number and password are erased and you are signed out.</li>
          <li>Your app rating, help messages and notification settings are removed.</li>
          <li>Any remaining plan time is lost and is not refunded.</li>
          <li>Payment records are kept anonymously, as required for accounting.</li>
          <li>This can&apos;t be undone. You can register again later with the same email.</li>
        </ul>
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-rose-100 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{error}</div>
      )}

      <form onSubmit={submit} className="mt-5 space-y-4">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-600">Your password</label>
          <PasswordInput
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="fs-input"
            placeholder="••••••••"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-600">Type DELETE to confirm</label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="fs-input"
            placeholder="DELETE"
            autoCapitalize="characters"
          />
        </div>
        <button
          type="submit"
          disabled={!ready || loading}
          className="w-full rounded-xl bg-rose-600 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {loading ? 'Deleting…' : 'Delete my account'}
        </button>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-full rounded-xl border border-slate-200 py-2.5 text-sm font-medium text-slate-700"
        >
          Cancel
        </button>
      </form>
    </div>
  );
}
